import React, { useState, useEffect, useRef } from "react";

// --- CSS --- //
import "../styles/popup-utils.css";

// --- ASSETS --- //
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SaveIcon from '@mui/icons-material/Save';

// --- COMPONENTS --- //
import Slider from "../components/Slider";
import { addCertificationInfo, fetchAdminPasscode, getAllEmployees, getEmployeeData } from "./firestore/firestoreUtils";
import { showMessageWithDelay } from "./alertUtils";

/**
 * A popup component for adding a new employee or editing an existing employee's details.
 * It includes input fields for the employee's name and 5-digit code, a toggle slider for 
 * their status, and buttons for saving or canceling the action.
 *
 * @param {Object} props - The props object.
 * @param {boolean} props.isOpen - Controls whether the popup is visible.
 * @param {function} props.onClose - Callback function to close the popup.
 * @param {function} props.addOrUpdateEmployee - Callback function to add a new employee 
 *                                               or update an existing one.
 * @param {Object|null} props.selectedEmployee - The employee object to edit. If `null`,
 *                                               the popup is used for adding a new employee.
 * 
 * @returns {JSX.Element|null} - The rendered popup JSX or `null` if `isOpen` is `false`.
 */
export const AddEditEmployeePopup = ({ isOpen, onClose, addOrUpdateEmployee, selectedEmployee }) => {
    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [status, setStatus] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        if (selectedEmployee) {
            setName(selectedEmployee.name || "");
            setCode(selectedEmployee.employeeCode || "");
            setStatus(selectedEmployee.status || true)
        } else {
            setName("");
            setCode("");
            setStatus(true);
        }
    }, [selectedEmployee]);

    const handleSaveClick = () => {
        if (!name || code.length !== 5) {
            setError("Please provide a valid name and 5-digit code");
            return;
        }

        const employee = {
            ...selectedEmployee, // Retain ID for existing employees
            name,
            employeeCode: code,
            status: status,
        };

        addOrUpdateEmployee(employee);
        setName("");
        setCode("");
        setStatus(true);
        setError("");
        onClose();
    };

    const handleClose = () => {
        setName("");
        setCode("");
        setStatus(true);
        setError("");
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="popup-overlay" onClick={handleClose}>
            <div className="popup-container" onClick={(e) => e.stopPropagation()}>
                <h4 className="popup-title">
                    {selectedEmployee ? "Edit Employee" : "Add New Employee"}
                </h4>
                <input
                    className="popup-input"
                    id="popup-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Employee Name"
                />
                <div className="popup-subcontainer">
                    <input
                        className="popup-input"
                        id="popup-pin"
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="Employee 5-Digit PIN"
                    />
                    <Slider status={status} onToggle={() => setStatus(!status)} />
                </div>
                <p className="popup-error">{error}</p>
                <div className="popup-action-container">
                    <button className="popup-button" id="popup-cancel" onClick={handleClose}>
                        Cancel
                    </button>
                    <button className="popup-button" id="popup-confirm" onClick={handleSaveClick}>
                        {selectedEmployee ? <SaveIcon style={{ marginRight: "7px" }} /> : <AddCircleIcon style={{ marginRight: "7px" }} />}
                        {selectedEmployee ? "Save" : "Add"}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * A modal component that displays a confirmation dialog with a customizable message 
 * and a list of selected employees (if applicable). It includes "Cancel" and "Delete" 
 * action buttons, and it is only visible when `isOpen` is true.
 *
 * @param {Object} props - The props object.
 * @param {boolean} props.isOpen - Controls whether the modal is visible.
 * @param {Array<Object>} props.employees - A list of employee objects, where each object
 *                                           contains at least `id` and `name` properties.
 * @param {Array<string>} props.employeeIds - A list of employee IDs that are selected 
 *                                            and should be displayed in the modal.
 * @param {string} props.message - The message to display in the modal.
 * @param {function} props.onConfirm - Callback function to execute when the "Delete" button is clicked.
 * @param {function} props.onCancel - Callback function to execute when the modal is closed 
 *                                     (either by clicking "Cancel" or the overlay).
 * 
 * @returns {JSX.Element|null} - The rendered modal JSX or `null` if `isOpen` is `false`.
 */
export const ActionConfirmationPopup = ({ isOpen, employees, employeeIds, message, onConfirm, onCancel }) => {
    if (!isOpen) return null;

    const selectedEmployeeNames = employees
        .filter((employee) => employeeIds.includes(employee.id))
        .map((employee) => employee.name);

    return (
        <div className="popup-overlay" onClick={onCancel}>
            <div className="popup-container" onClick={(e) => e.stopPropagation()}>
                <p className="popup-message">{message}</p>
                {selectedEmployeeNames && selectedEmployeeNames.length > 0 && (
                    <ul className="popup-list">
                        {selectedEmployeeNames.map((name, index) => (
                            <li key={index} className="popup-list-item">
                                {name}
                            </li>
                        ))}
                    </ul>
                )}
                <div className="popup-action-container">
                    <button className="popup-button" id="popup-cancel" onClick={onCancel}>
                        Cancel
                    </button>
                    <button className="popup-button" id="popup-delete" onClick={onConfirm}>
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

export const EmployeeVerificationPopup = ({ isEmployeeVerificationOpen, onEmployeeVerificationClose, certificationData, certUserID }) => {
    const [error, setError] = useState("");
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const inputRefs = useRef([]);

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const employeeList = await getAllEmployees();
                const employeeData = await getEmployeeData(employeeList);

                if (employeeList && Array.isArray(employeeList)) {
                    const activeEmployees = employeeData.filter((employee) => employee.status === true);
                    setEmployees(activeEmployees); // Populate dropdown with employee list
                } else {
                    setError("Failed to load employees. Invalid response.");
                }
            } catch (err) {
                setError("Failed to load employees. Please try again.");
            }
        };

        fetchEmployees();

        if (isEmployeeVerificationOpen) {
            fetchEmployees();
            setSelectedEmployee(null); // Reset selected employee
            inputRefs.current.forEach((input) => (input.value = "")); // Clear input fields
        }
    }, [isEmployeeVerificationOpen]);

    const handleSaveClick = () => {
        if (!selectedEmployee) {
            setError("Please select an employee before entering the code.");
            return;
        }

        const code = inputRefs.current.map((input) => input?.value).join("");
        if (code.length !== 5) {
            setError("Please provide a valid 5-digit code.");
            return;
        }

        const verifyEmployee = async () => {
            try {
                // Fetch employee data for verification using selectedEmployee.id
                const employeeData = await getEmployeeData([selectedEmployee.id]);
                const employee = employeeData.find((emp) => emp.employeeCode === code);

                if (!employee) {
                    setError("Invalid employee code.");
                    return;
                }

                // Code is valid, proceed with certification info
                try {
                    if (certificationData.length > 0) {
                        await addCertificationInfo(certUserID, certificationData, employee);

                        setError(""); // Clear any previous errors
                        onEmployeeVerificationClose(); // Close popup or perform further actions
                        showMessageWithDelay("Certifications Added", "Certification were successfully added to the users account!", "success", 3000);
                    } else {
                        setError("No certifications selected.");
                    }
                } catch (err) {
                    setError("Error processing certification data: " + err.message);
                }
            } catch (err) {
                setError("An error occurred while verifying the code. Please try again.");
            }
        };

        verifyEmployee();
    };

    const handleClose = () => {
        setError("");
        onEmployeeVerificationClose();
    };

    const handleInputChange = (e, index) => {
        const { value } = e.target;

        if (value.length === 1) {
            // Move to the next input
            if (index < inputRefs.current.length - 1) {
                inputRefs.current[index + 1]?.focus();
            } else {
                // Focus the "Certify Now" button on the last input
                document.getElementById("popup-confirm")?.focus();
            }
        }
    };

    const handleInputKeyDown = (e, index) => {
        if (e.key === "Backspace" && index > 0 && !e.target.value) {
            // Move focus to the previous input on backspace if current is empty
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleEmployeeChange = (e) => {
        const selectedEmployeeId = e.target.value;
        const employee = employees.find(emp => emp.id === selectedEmployeeId);
        setSelectedEmployee(employee);
        setError(""); // Clear any previous errors
    };

    if (!isEmployeeVerificationOpen) return null;

    return (
        <div className="popup-overlay" onClick={handleClose}>
            <div className="popup-container" onClick={(e) => e.stopPropagation()}>
                <h4 className="popup-title">Employee Verification</h4>
                <ul className="popup-list" style={{ marginBottom: "20px" }}>
                    {certificationData.map((cert, index) => (
                        <li className="popup-list-item" key={index}>
                            {cert.name}
                        </li>
                    ))}
                </ul>
                <select
                    className="popup-dropdown"
                    value={selectedEmployee?.id || ""}
                    onChange={handleEmployeeChange}
                >
                    <option value="" disabled>
                        Select Employee
                    </option>
                    {employees.length > 0 ? (
                        employees.map((employee) => (
                            <option key={employee.id} value={employee.id}>
                                {employee.name}
                            </option>
                        ))
                    ) : (
                        <option value="">No employees available</option>
                    )}
                </select>
                <div className="popup-code-container">
                    {Array.from({ length: 5 }).map((_, index) => (
                        <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            className="popup-code-input"
                            placeholder="-"
                            maxLength={1}
                            onChange={(e) => handleInputChange(e, index)}
                            onKeyDown={(e) => handleInputKeyDown(e, index)}
                            disabled={!selectedEmployee} // Disable inputs if no employee selected
                        />
                    ))}
                </div>
                <p className="popup-error">{error}</p>
                <div className="popup-action-container">
                    <button className="popup-button" id="popup-cancel" onClick={handleClose}>
                        Cancel
                    </button>
                    <button
                        className="popup-button"
                        id="popup-confirm"
                        onClick={handleSaveClick}
                        disabled={!selectedEmployee} // Disable button if no employee selected
                    >
                        Certify Now
                    </button>
                </div>
            </div>
        </div>
    );
};

export const AdminVerificationPopup = ({
    isVerificationOpen,
    onVerificationClose,
    setIsAuthorized,
}) => {
    const [error, setError] = useState("");
    const [adminPasscode, setAdminPasscode] = useState(""); // State for admin passcode
    const inputRefs = useRef([]); // Create refs for inputs
    const verifyButtonRef = useRef(null); // Ref for the verify button

    useEffect(() => {
        const handleFetchAdminPasscode = async () => {
            try {
                const code = await fetchAdminPasscode();
                setAdminPasscode(code); // Update state
            } catch (err) {
                setError("Failed to fetch admin passcode. Please try again.");
            }
        };

        if (isVerificationOpen) {
            handleFetchAdminPasscode();
            inputRefs.current.forEach((input) => (input.value = ""));
            
            // Focus the first input field
            inputRefs.current[0]?.focus();
        }
    }, [isVerificationOpen]); // Run this effect when `isVerificationOpen` changes

    const handleVerifyClick = () => {
        const enteredCode = inputRefs.current.map((input) => input?.value).join("");
        if (adminPasscode === "") {
            setError("An error occurred. Please try again later.");
            return;
        }

        if (enteredCode !== adminPasscode) {
            setError("Invalid admin passcode.");
            return;
        }

        setIsAuthorized(true);
        onVerificationClose();
        showMessageWithDelay(
            "Access Granted",
            "Admin passcode verified successfully!",
            "success",
            1500
        );
    };

    const handleInputChange = (e, index) => {
        const { value } = e.target;

        if (value.length === 1) {
            if (index < inputRefs.current.length - 1) {
                inputRefs.current[index + 1]?.focus(); // Focus next input
            } else {
                verifyButtonRef.current?.focus(); // Focus the Verify button when the last field is filled
            }
        }
    };

    const handleInputKeyDown = (e, index) => {
        if (e.key === "Backspace" && index > 0 && !e.target.value) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    if (!isVerificationOpen) return null;

    return (
        <div className="popup-overlay">
            <div className="popup-container" onClick={(e) => e.stopPropagation()}>
                <h4 className="popup-title" style={{textAlign: "center"}}>Admin Verification</h4>
                <div className="popup-code-container">
                    {Array.from({ length: 5 }).map((_, index) => (
                        <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)} // Assign refs
                            className="popup-code-input"
                            placeholder="-"
                            maxLength={1}
                            onChange={(e) => handleInputChange(e, index)}
                            onKeyDown={(e) => handleInputKeyDown(e, index)}
                        />
                    ))}
                </div>
                <p className="popup-error">{error}</p>
                <div className="popup-action-container">
                    <button
                    style={{width: "100%"}}
                        className="popup-button"
                        id="popup-confirm"
                        onClick={handleVerifyClick}
                        ref={verifyButtonRef} // Attach ref to Verify button
                    >
                        Verify
                    </button>
                </div>
            </div>
        </div>
    );
};